import { collection, onSnapshot, query, where } from 'firebase/firestore';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { db } from '../firebase';

export interface Notification {
  id: string;
  type: 'message' | 'appointment' | 'system';
  title: string;
  message: string;
  senderId?: string;
  senderName?: string;
  receiverId?: string;
  conversationId?: string;
  timestamp: Date;
  isRead: boolean;
  link?: string;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  unreadMessageCount: number;
  markAsRead: (notificationId: string) => void;
  markAllAsRead: () => void;
  clearNotification: (notificationId: string) => void;
  clearNotificationsBySender: (senderId: string) => void;
  clearNotificationsByMessageIds: (messageIds: string[]) => void;
  clearAll: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

export const NotificationProvider: React.FC<{
  children: React.ReactNode;
  currentUserId?: string;
  userRole?: 'dietitian' | 'client';
}> = ({ children, currentUserId, userRole }) => {
  // localStorage'dan bildirimleri yükle
  const getStoredNotifications = useCallback((): Notification[] => {
    if (!currentUserId) return [];
    try {
      const storageKey = `notifications_${currentUserId}`;
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Timestamp'leri Date'e çevir
        return parsed.map((n: Notification) => ({
          ...n,
          timestamp: new Date(n.timestamp),
        }));
      }
    } catch (error) {
      console.error('Bildirimler yüklenirken hata:', error);
    }
    return [];
  }, [currentUserId]);

  const [notifications, setNotifications] = useState<Notification[]>([]);

  // Bildirimleri localStorage'a kaydet
  const saveNotifications = useCallback(
    (notifs: Notification[]) => {
      if (!currentUserId) return;
      try {
        const storageKey = `notifications_${currentUserId}`;
        localStorage.setItem(storageKey, JSON.stringify(notifs));
      } catch (error) {
        console.error('Bildirimler kaydedilirken hata:', error);
      }
    },
    [currentUserId]
  );

  // currentUserId değiştiğinde localStorage'dan bildirimleri yükle
  useEffect(() => {
    if (!currentUserId) {
      setNotifications([]);
      return;
    }
    const stored = getStoredNotifications();
    setNotifications(stored);
  }, [currentUserId, getStoredNotifications]);

  const CLEARED_SYSTEM_IDS_KEY = (uid: string) => `clearedSystemNotifications_${uid}`;
  const MAX_CLEARED_IDS = 300;

  const getClearedSystemIds = useCallback((): Set<string> => {
    if (!currentUserId) return new Set();
    try {
      const raw = localStorage.getItem(CLEARED_SYSTEM_IDS_KEY(currentUserId));
      const arr = raw ? JSON.parse(raw) : [];
      return new Set(Array.isArray(arr) ? arr : []);
    } catch {
      return new Set();
    }
  }, [currentUserId]);

  const addClearedSystemIds = useCallback((ids: string[]) => {
    if (!currentUserId || ids.length === 0) return;
    try {
      const key = CLEARED_SYSTEM_IDS_KEY(currentUserId);
      const current = getClearedSystemIds();
      ids.forEach((id) => current.add(id));
      const arr = Array.from(current);
      const trimmed = arr.length > MAX_CLEARED_IDS ? arr.slice(-MAX_CLEARED_IDS) : arr;
      localStorage.setItem(key, JSON.stringify(trimmed));
    } catch (e) {
      console.error('clearedSystemNotifications kaydedilemedi:', e);
    }
  }, [currentUserId, getClearedSystemIds]);

  const handleSystemNotificationSnapshot = useCallback((snapshot: any) => {
    const clearedIds = getClearedSystemIds();
    setNotifications((prevNotifications) => {
      // Mevcut MESAJ bildirimlerini ayıkla (bunları koruyacağız)
      const messageNotifications = prevNotifications.filter(n => n.type === 'message');

      // Mevcut SİSTEM bildirimlerini ID'lerine göre map'le
      const existingSystemNotificationsMap = new Map<string, Notification>();
      prevNotifications.filter(n => n.type !== 'message').forEach(n => {
        existingSystemNotificationsMap.set(n.id, n);
      });

      const newSystemNotifications: Notification[] = [];

      snapshot.forEach((doc: any) => {
        const notificationId = doc.id;
        if (clearedIds.has(notificationId)) return;
        const data = doc.data();

        // Eğer bildirim firestore'da okundu olarak işaretlenmişse ve bizde de varsa, durumunu güncelle
        // Ancak bizim context'te isRead yönetimi local olduğu için, firestore'dan silinmediği sürece gösterelim
        // Not: Backend tarafı bildirim silmiyor, o yüzden isRead kontrolü önemli olabilir ama
        // sistem bildirimlerinde kullanıcı 'çarpı'ya bastığında localden siliyor.

        // Şimdilik basit yaklaşım: Firestore'daki tüm sistem bildirimlerini al, 
        // localde okundu olanların durumunu koru.

        const timestamp = data.CreatedAt?.toDate
          ? data.CreatedAt.toDate()
          : (data.timestamp?.toDate ? data.timestamp.toDate() : new Date());

        // Eski bildirimdeki isRead durumunu koru (eğer varsa)
        const existingNotif = existingSystemNotificationsMap.get(notificationId);
        const isRead = existingNotif ? existingNotif.isRead : (data.IsRead || false);

        const notification: Notification = {
          id: notificationId,
          type: (data.Type as 'message' | 'appointment' | 'system') || 'system',
          title: data.Title || 'Yeni Bildirim',
          message: data.Message || '',
          senderId: data.SenderId,
          senderName: data.SenderName,
          receiverId: data.ReceiverId,
          timestamp: timestamp,
          isRead: isRead,
          link: data.Link
        };

        newSystemNotifications.push(notification);
      });

      // Birleştir ve Sırala
      const combinedNotifications = [...messageNotifications, ...newSystemNotifications];
      combinedNotifications.sort(
        (a, b) => b.timestamp.getTime() - a.timestamp.getTime()
      );

      saveNotifications(combinedNotifications);
      return combinedNotifications;
    });
  }, [saveNotifications, getClearedSystemIds]);
  const handleMessageSnapshot = useCallback((snapshot: any) => {
    setNotifications((prevNotifications) => {
      // Her seferinde localStorage'dan güncel processedMessageIds'i yükle
      if (!currentUserId) return prevNotifications;

      const storageKey = `processedMessages_${currentUserId}`;
      const savedProcessedIds = localStorage.getItem(storageKey);
      const currentProcessedIds = new Set<string>(
        savedProcessedIds ? JSON.parse(savedProcessedIds) : []
      );

      // Mevcut SİSTEM bildirimlerini ayıkla (bunları koruyacağız)
      const systemNotifications = prevNotifications.filter(n => n.type !== 'message');

      // Mevcut MESAJ bildirimlerini ayıkla
      const existingMessageNotifications = prevNotifications.filter(n => n.type === 'message');
      const existingMessageNotificationIds = new Set(existingMessageNotifications.map(n => n.id));

      const newMessageNotifications: Notification[] = [...existingMessageNotifications];
      const newProcessedIds = new Set(currentProcessedIds);
      let hasNewNotification = false;

      snapshot.forEach((doc: any) => {
        const data = doc.data();
        const messageId = doc.id;
        const isRead = data.isRead || false;

        // Eğer bu mesaj için zaten bildirim varsa
        if (existingMessageNotificationIds.has(messageId)) {
          // Eğer mesaj okundu olarak işaretlenmişse, bildirimi kaldır
          if (isRead) {
            const index = newMessageNotifications.findIndex(
              (n) => n.id === messageId
            );
            if (index >= 0) {
              newMessageNotifications.splice(index, 1);
            }
          }
          return;
        }

        // Eğer bu mesaj daha önce işlenmişse, atla
        if (newProcessedIds.has(messageId)) {
          return;
        }

        // Eğer mesaj zaten okundu olarak işaretlenmişse, bildirim oluşturma
        if (isRead) {
          newProcessedIds.add(messageId);
          return;
        }

        // Mesaj zamanını kontrol et (7 gün)
        const messageTime = data.timestamp?.toDate
          ? data.timestamp.toDate()
          : new Date(data.timestamp);
        const now = new Date();
        const timeDiff = now.getTime() - messageTime.getTime();

        if (timeDiff < 7 * 24 * 60 * 60 * 1000) {
          const senderName =
            data.senderName || data.senderId || 'Bilinmeyen Kullanıcı';
          const messagePreview =
            data.content?.length > 50
              ? data.content.substring(0, 50) + '...'
              : data.content || '';

          const notification: Notification = {
            id: messageId,
            type: 'message',
            title: 'Yeni Mesaj',
            message: `${senderName}: ${messagePreview}`,
            senderId: data.senderId,
            senderName: senderName,
            receiverId: data.receiverId,
            conversationId: data.conversationId,
            timestamp: messageTime,
            isRead: false,
            link:
              userRole === 'dietitian'
                ? '/dietitian/messages'
                : '/client/messages',
          };

          newMessageNotifications.push(notification);
          hasNewNotification = true;
          newProcessedIds.add(messageId);
          existingMessageNotificationIds.add(messageId);
        }
      });

      if (hasNewNotification) {
        localStorage.setItem(
          storageKey,
          JSON.stringify(Array.from(newProcessedIds))
        );
      }

      // Sistem ve Mesaj bildirimlerini birleştir
      const combinedNotifications = [...systemNotifications, ...newMessageNotifications];

      // Sırala
      combinedNotifications.sort(
        (a, b) => b.timestamp.getTime() - a.timestamp.getTime()
      );

      saveNotifications(combinedNotifications);
      return combinedNotifications;
    });
  }, [currentUserId, userRole, saveNotifications]);


  useEffect(() => {
    if (!currentUserId || !userRole) return;

    // Firebase'den Mesajları Dinle
    const messagesQuery = query(
      collection(db, 'messages'),
      where('receiverId', '==', currentUserId)
    );

    // Firebase'den Sistem Bildirimlerini Dinle (Notifications collection)
    const notificationsQuery = query(
      collection(db, 'Notifications'),
      where('ReceiverId', '==', currentUserId)
    );

    const unsubscribeMessages = onSnapshot(
      messagesQuery,
      (snapshot) => {
        handleMessageSnapshot(snapshot);
      },
      (error) => {
        console.error('Mesaj dinleme hatası:', error);
      }
    );

    const unsubscribeNotifications = onSnapshot(
      notificationsQuery,
      (snapshot) => {
        handleSystemNotificationSnapshot(snapshot);
      },
      (error) => {
        console.error('Sistem bildirimi dinleme hatası:', error);
      }
    );

    return () => {
      unsubscribeMessages();
      unsubscribeNotifications();
    };
  }, [currentUserId, userRole, saveNotifications, handleMessageSnapshot, handleSystemNotificationSnapshot]);

  // Mesaj snapshot işleyici (ayrı fonksiyon olarak tanımlandı)


  const markAsRead = useCallback(
    (notificationId: string) => {
      setNotifications((prev) => {
        const updated = prev.map((notif) =>
          notif.id === notificationId ? { ...notif, isRead: true } : notif
        );
        saveNotifications(updated);
        return updated;
      });
    },
    [saveNotifications]
  );

  const markAllAsRead = useCallback(() => {
    setNotifications((prev) => {
      const updated = prev.map((notif) => ({ ...notif, isRead: true }));
      saveNotifications(updated);
      return updated;
    });
  }, [saveNotifications]);

  const clearNotification = useCallback(
    (notificationId: string) => {
      setNotifications((prev) => {
        const notificationToRemove = prev.find((n) => n.id === notificationId);
        const updated = prev.filter((notif) => notif.id !== notificationId);
        saveNotifications(updated);

        if (notificationToRemove && currentUserId) {
          if (notificationToRemove.type === 'message') {
            const storageKey = `processedMessages_${currentUserId}`;
            const savedProcessedIds = localStorage.getItem(storageKey);
            const processedIds = new Set<string>(
              savedProcessedIds ? JSON.parse(savedProcessedIds) : []
            );
            processedIds.add(notificationId);
            localStorage.setItem(
              storageKey,
              JSON.stringify(Array.from(processedIds))
            );
          } else {
            addClearedSystemIds([notificationId]);
          }
        }

        return updated;
      });
    },
    [saveNotifications, currentUserId, addClearedSystemIds]
  );

  const clearNotificationsBySender = useCallback(
    (senderId: string) => {
      setNotifications((prev) => {
        // Bu gönderenin tüm mesaj bildirimlerini kaldır
        const notificationsToRemove = prev.filter(
          (notif) => notif.type === 'message' && notif.senderId === senderId
        );
        const updated = prev.filter(
          (notif) => notif.type !== 'message' || notif.senderId !== senderId
        );
        saveNotifications(updated);

        // Temizlenen bildirimlerin mesaj ID'lerini processedMessageIds'e ekle
        if (currentUserId && notificationsToRemove.length > 0) {
          const storageKey = `processedMessages_${currentUserId}`;
          const savedProcessedIds = localStorage.getItem(storageKey);
          const processedIds = new Set<string>(
            savedProcessedIds ? JSON.parse(savedProcessedIds) : []
          );
          notificationsToRemove.forEach((notif) => {
            processedIds.add(notif.id);
          });
          localStorage.setItem(
            storageKey,
            JSON.stringify(Array.from(processedIds))
          );
        }

        return updated;
      });
    },
    [saveNotifications, currentUserId]
  );

  const clearNotificationsByMessageIds = useCallback(
    (messageIds: string[]) => {
      setNotifications((prev) => {
        // Bu mesaj ID'lerine sahip tüm mesaj bildirimlerini kaldır
        const messageIdSet = new Set(messageIds);
        const updated = prev.filter(
          (notif) => notif.type !== 'message' || !messageIdSet.has(notif.id)
        );
        saveNotifications(updated);

        // Temizlenen bildirimlerin mesaj ID'lerini processedMessageIds'e ekle
        if (currentUserId && messageIds.length > 0) {
          const storageKey = `processedMessages_${currentUserId}`;
          const savedProcessedIds = localStorage.getItem(storageKey);
          const processedIds = new Set<string>(
            savedProcessedIds ? JSON.parse(savedProcessedIds) : []
          );
          messageIds.forEach((id) => processedIds.add(id));
          localStorage.setItem(
            storageKey,
            JSON.stringify(Array.from(processedIds))
          );
        }

        return updated;
      });
    },
    [saveNotifications, currentUserId]
  );

  const clearAll = useCallback(() => {
    setNotifications((prev) => {
      if (currentUserId && prev.length > 0) {
        const storageKey = `processedMessages_${currentUserId}`;
        const savedProcessedIds = localStorage.getItem(storageKey);
        const processedIds = new Set<string>(
          savedProcessedIds ? JSON.parse(savedProcessedIds) : []
        );
        const systemIdsToClear: string[] = [];
        prev.forEach((notif) => {
          if (notif.type === 'message') {
            processedIds.add(notif.id);
          } else {
            systemIdsToClear.push(notif.id);
          }
        });
        localStorage.setItem(
          storageKey,
          JSON.stringify(Array.from(processedIds))
        );
        addClearedSystemIds(systemIdsToClear);
      }

      const notificationsKey = `notifications_${currentUserId}`;
      if (currentUserId) {
        localStorage.removeItem(notificationsKey);
      }

      return [];
    });
  }, [currentUserId, addClearedSystemIds]);

  const unreadCount = notifications.filter((n) => !n.isRead).length;
  const unreadMessageCount = notifications.filter((n) => !n.isRead && n.type === 'message').length;

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        unreadMessageCount,
        markAsRead,
        markAllAsRead,
        clearNotification,
        clearNotificationsBySender,
        clearNotificationsByMessageIds,
        clearAll,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      'useNotification must be used within a NotificationProvider'
    );
  }
  return context;
};
